/**
 * Profile Basics Step
 *
 * Name and privacy settings collection.
 * Issue #563 - First-Run Onboarding Flow
 * Issue #703 - Public name display mode options
 * Issue #737 - Avatar upload during onboarding
 */

import { useState, useRef } from 'react';
import { User, ChevronLeft, ArrowRight, Camera } from 'lucide-react';
import { toast } from 'sonner';
import type { Me } from '@/lib/api/auth';
import { profileApi, type ProfileUpdateRequest } from '@/lib/api/profile';
import { ApiError } from '@/lib/api';
import { useProfileImageUpload } from '@/hooks/useProfileImageUpload';
import { getUserFacingError } from '@/lib/errorMessages';
import AvatarCropModal from '../../AvatarCropModal';

interface ProfileBasicsStepProps {
  user: Me;
  onNext: () => void;
  onBack: () => void;
}

type PublicNameMode = 'full' | 'first_last_initial' | 'first_only' | 'anonymous';

// Issue #919: Anonymous mode disabled for v1.0 - needs additional UX consideration
const NAME_MODE_OPTIONS: { value: PublicNameMode; label: string }[] = [
  { value: 'full', label: 'Full Name' },
  { value: 'first_last_initial', label: 'First + Initial' },
  { value: 'first_only', label: 'First Name Only' },
];

export default function ProfileBasicsStep({ user, onNext, onBack }: ProfileBasicsStepProps) {
  const [firstName, setFirstName] = useState(user.first_name || '');
  const [lastName, setLastName] = useState(user.last_name || '');
  const [publicNameMode, setPublicNameMode] = useState<PublicNameMode>(
    (user.public_name_mode as PublicNameMode) || 'first_last_initial'
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Avatar upload state (Issue #737)
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string | null>(user.profile_image_url);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { uploadImage, isUploading } = useProfileImageUpload();

  // Validation
  const canProceed = firstName.trim() && lastName.trim();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canProceed || isSubmitting) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const updateData: ProfileUpdateRequest = {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        public_name_mode: publicNameMode,
      };

      await profileApi.updateProfile(updateData);
      onNext();
    } catch (err) {
      // Use ApiError message if available (standard error envelope)
      const message = err instanceof ApiError
        ? err.message
        : 'Failed to save profile. Please try again.';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Generate preview based on current inputs and optional mode override
  const getPreviewForMode = (mode: PublicNameMode): string => {
    const first = firstName.trim() || 'John';
    const last = lastName.trim() || 'Smith';
    switch (mode) {
      case 'full':
        return `${first} ${last}`;
      case 'first_last_initial':
        return `${first} ${last.charAt(0)}.`;
      case 'first_only':
        return first;
      case 'anonymous':
        return 'Anonymous';
      default:
        return `${first} ${last.charAt(0)}.`;
    }
  };

  // Current selection preview (shows placeholder if no name entered)
  const getPreview = (): string => {
    const first = firstName.trim() || 'Your';
    const last = lastName.trim() || 'Name';
    switch (publicNameMode) {
      case 'full':
        return `${first} ${last}`;
      case 'first_last_initial':
        return `${first} ${last.charAt(0)}.`;
      case 'first_only':
        return first;
      case 'anonymous':
        return 'Anonymous';
      default:
        return `${first} ${last.charAt(0)}.`;
    }
  };

  // Avatar upload handlers (Issue #737)
  const handleCameraClick = () => {
    fileInputRef.current?.click();
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error(getUserFacingError('INVALID_FILE_TYPE', 'Please select an image file'));
      return;
    }

    // Validate file size (20MB - CF Image Resizing handles optimization #792)
    const maxSize = 20 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(getUserFacingError('FILE_TOO_LARGE', 'Image must be smaller than 20MB'));
      return;
    }

    // Open crop modal
    setPendingFile(file);
    setShowCropModal(true);
  };

  const handleCropComplete = async (croppedFile: File) => {
    setShowCropModal(false);
    setPendingFile(null);

    try {
      await uploadImage(croppedFile);
      // Create local URL to display immediately (hook invalidates queries but prop won't update)
      const localUrl = URL.createObjectURL(croppedFile);
      setUploadedImageUrl(localUrl);
      toast.success('Profile photo updated');
    } catch (err) {
      const apiError = err as ApiError;
      toast.error(getUserFacingError(apiError.code || 'UPLOAD_FAILED', apiError.message || 'Failed to upload image'));
    }
  };

  const handleCropCancel = () => {
    setShowCropModal(false);
    setPendingFile(null);
  };

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Progress Bar - Step 2 of 6 */}
      <div className="shrink-0 px-6 pt-4">
        <div className="w-full h-1 bg-oro-surface-alt rounded-full overflow-hidden">
          <div
            className="h-full bg-oro-gold rounded-full transition-all duration-300"
            style={{ width: '33%' }}
          />
        </div>
        <p className="text-text-muted text-xs mt-2 text-center">Step 2 of 6</p>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-6">
        {/* Avatar Header with Upload (Issue #737) */}
        <div className="text-center mb-6">
          <div className="relative w-16 h-16 mx-auto mb-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-oro-gold to-oro-gold-dark flex items-center justify-center shadow-lg overflow-hidden ring-2 ring-oro-border">
              {uploadedImageUrl ? (
                <img
                  src={uploadedImageUrl}
                  alt="Your avatar"
                  className="w-full h-full object-cover"
                />
              ) : (
                <User className="w-8 h-8 text-black" />
              )}
            </div>
            <button
              type="button"
              onClick={handleCameraClick}
              disabled={isUploading || isSubmitting}
              className={`absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-oro-accent shadow-lg flex items-center justify-center transition-all ${
                isUploading || isSubmitting
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-oro-accent/90 hover:scale-110'
              }`}
            >
              <Camera className={`w-4 h-4 text-white ${isUploading ? 'animate-pulse' : ''}`} />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
            />
          </div>
          <h2 id="onboarding-title" className="text-xl font-semibold text-text-primary mb-2">
            Your Profile
          </h2>
          <p className="text-text-muted text-sm">Add a photo and set your display name</p>
        </div>

        {/* Form */}
        <form id="profile-form" onSubmit={handleSubmit} className="space-y-4">
          {/* Name Fields */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-oro-surface border border-oro-border rounded-xl p-3">
              <label htmlFor="firstName" className="text-text-secondary text-xs">
                First Name
              </label>
              <input
                id="firstName"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="John"
                maxLength={50}
                className="w-full bg-transparent text-text-primary placeholder:text-text-muted outline-none text-sm mt-1"
                disabled={isSubmitting}
                autoFocus
              />
            </div>
            <div className="bg-oro-surface border border-oro-border rounded-xl p-3">
              <label htmlFor="lastName" className="text-text-secondary text-xs">
                Last Name
              </label>
              <input
                id="lastName"
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Smith"
                maxLength={50}
                className="w-full bg-transparent text-text-primary placeholder:text-text-muted outline-none text-sm mt-1"
                disabled={isSubmitting}
              />
            </div>
          </div>

          {/* Public Name Mode (Issue #703) */}
          <div className="bg-oro-surface border border-oro-border rounded-xl p-4">
            <label className="text-text-secondary text-sm block mb-3">
              Public Display Name
            </label>
            <div className="space-y-2">
              {NAME_MODE_OPTIONS.map((option) => (
                <label
                  key={option.value}
                  className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${
                    publicNameMode === option.value
                      ? 'bg-oro-gold/10 border border-oro-gold'
                      : 'bg-oro-surface-alt border border-transparent hover:border-oro-border'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="radio"
                      name="publicNameMode"
                      value={option.value}
                      checked={publicNameMode === option.value}
                      onChange={() => setPublicNameMode(option.value)}
                      disabled={isSubmitting}
                      className="sr-only"
                    />
                    <div
                      className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        publicNameMode === option.value
                          ? 'border-oro-gold'
                          : 'border-text-muted'
                      }`}
                    >
                      {publicNameMode === option.value && (
                        <div className="w-2 h-2 rounded-full bg-oro-gold" />
                      )}
                    </div>
                    <span className="text-text-primary text-sm font-medium">
                      {option.label}
                    </span>
                  </div>
                  <span className="text-text-muted text-sm">{getPreviewForMode(option.value)}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Preview */}
          {canProceed && (
            <div className="bg-oro-surface-alt border border-oro-border rounded-xl p-4 text-center">
              <p className="text-text-muted text-xs mb-1">Others will see you as</p>
              <p className="text-text-primary font-semibold">{getPreview()}</p>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="p-4 bg-oro-error/10 border border-oro-error/20 rounded-xl">
              <p className="text-sm text-oro-error">{error}</p>
            </div>
          )}
        </form>
      </div>

      {/* Bottom-Docked Actions */}
      <div className="shrink-0 p-4 border-t border-oro-border bg-oro-surface">
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onBack}
            disabled={isSubmitting}
            className="px-4 py-3 rounded-lg bg-oro-surface-alt border border-oro-border text-text-primary hover:bg-oro-surface-alt/80 transition-colors disabled:opacity-50"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            type="submit"
            form="profile-form"
            disabled={isSubmitting || !canProceed}
            className={`flex-1 px-4 py-3 rounded-lg flex items-center justify-center gap-2 transition-all ${
              canProceed && !isSubmitting
                ? 'bg-oro-gold hover:bg-oro-gold/90 shadow-lg'
                : 'bg-oro-surface-alt border border-oro-border text-text-muted cursor-not-allowed'
            }`}
          >
            <span className={canProceed && !isSubmitting ? 'text-black font-medium' : ''}>
              {isSubmitting ? 'Saving...' : 'Continue'}
            </span>
            {!isSubmitting && <ArrowRight className={`w-5 h-5 ${canProceed ? 'text-black' : ''}`} />}
          </button>
        </div>
      </div>

      {/* Avatar Crop Modal (Issue #737) */}
      {pendingFile && (
        <AvatarCropModal
          isOpen={showCropModal}
          onClose={handleCropCancel}
          imageFile={pendingFile}
          onCropComplete={handleCropComplete}
        />
      )}
    </div>
  );
}
